import { firstValueFrom, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { filter, map, share, withLatestFrom } from 'rxjs/operators';
import { FlowSubject } from '@equinor/fusion-observable';
import { filterAction } from '@equinor/fusion-observable/operators';
import actions from './actions';
import { handleRequests } from './handle-requests';
import { handleExecution } from './handle-execution';
import { handleFailure } from './handle-failure';
import { QueryClientError } from './QueryClientError';
import createReducer from './create-reducer';
import { QueryClientJob } from './QueryClientJob';
import { QueryClientEvent, } from './QueryClientEvent';
/**
 * QueryClient is a generic class that manages the state of asynchronous queries
 * and their lifecycle. It extends Observable to allow subscribers to react to state changes.
 *
 * It uses a FlowSubject to manage a stream of actions that describe the state changes and
 * side effects such as logging. The internal state is managed by a reducer function that
 * processes these actions.
 *
 * The QueryClient provides several specialized Observables (success$, error$, etc.) to
 * observe particular aspects of the query lifecycle, such as successful results or errors.
 *
 * @template TType The type of the data returned by the query function.
 * @template TArgs The type of the arguments passed to the query function.
 */
export class QueryClient extends Observable {
    /** Internal state managed by a FlowSubject. */
    #state;
    /** Subscription to manage cleanup of resources on completion. */
    #subscription;
    /**
     * Events subject for emitting QueryClient lifecycle events.
     *
     * This subject broadcasts events that track query execution stages,
     * allowing external observers to monitor query progress, success, and failures.
     */
    #events;
    /**
     * Protected method to emit QueryClient lifecycle events.
     *
     * This method creates and emits events that track the various stages of query execution,
     * allowing external observers to monitor query progress, success, failure, and cancellation.
     *
     * @template TType - The specific event type from QueryClientEvents
     * @param type - The event type identifier
     * @param transaction - Unique transaction identifier for the query operation
     * @param data - Optional event-specific data payload (type-safe based on event type)
     * @protected
     */
    _registerEvent(type, transaction, data) {
        this.#events.next(new QueryClientEvent(type, transaction, data));
    }
    /**
     * Indicates whether the QueryClient has been closed and is no longer accepting new actions.
     * @returns {boolean} A boolean value indicating if the state is closed.
     */
    get closed() {
        return this.#state.closed;
    }
    /**
     * An Observable of the current state. It emits the current state of the query client
     * whenever it changes.
     * @returns {Observable<QueryClientState<TArgs>>} An Observable stream of the current state.
     */
    get state$() {
        return this.#state.asObservable();
    }
    /**
     * An Observable stream of dispatched actions. It allows subscribers to react to
     * specific actions that are dispatched in the system.
     * @returns {Observable<Actions<TType, TArgs>>} An Observable stream of actions.
     */
    get action$() {
        return this.#state.action$;
    }
    /**
     * An Observable stream of QueryClient lifecycle events.
     *
     * This stream emits events that track the various stages of query execution,
     * including job requested, executing, completed, failed, canceled, and error events.
     * Subscribers can monitor query progress and handle different lifecycle events.
     *
     * @returns An Observable stream of QueryClient events
     */
    get event$() {
        return this.#events.asObservable();
    }
    /**
     * An Observable stream of successful query results. It emits the result of a query
     * when it completes successfully.
     * @returns {Observable<TType>} An Observable stream of successful results.
     */
    get success$() {
        // narrow the action stream down to successful execute actions and extract their payload
        return this.action$.pipe(filter(actions.execute.success.match), map(({ payload }) => payload));
    }
    /**
     * An Observable stream of query errors. It emits an instance of QueryClientError
     * when a query fails to complete.
     * @returns {Observable<QueryClientError>} An Observable stream of query errors.
     */
    get error$() {
        // narrow the action stream down to client errors and wrap them as QueryClientError instances
        return this.action$.pipe(filterAction('client/error'), withLatestFrom(this.#state), map(([action, state]) => {
            const { payload, meta: { transaction }, } = action;
            const request = state[transaction];
            return new QueryClientError('error', {
                request,
                message: `job: ${transaction} failed`,
                cause: payload,
            });
        }));
    }
    /**
     * Creates an instance of QueryClient.
     * @param {QueryFn<TType, TArgs>} queryFn A function that performs the asynchronous query.
     * @param {QueryClientCtorOptions} [options] Optional configuration options for the QueryClient.
     */
    constructor(queryFn, options) {
        super((subscriber) => {
            return this.#state.subscribe(subscriber);
        });
        this.#state = new FlowSubject(createReducer({}));
        // Set up subscription for cleanup, ensuring both state and events subjects are completed
        this.#subscription = new Subscription(() => {
            this.#state.complete();
            this.#events.complete();
        });
        // Initialize events subject for broadcasting lifecycle events
        this.#events = new Subject();
        // Add flows to handle different aspects of the query lifecycle.
        this.#subscription.add(this.#state.addFlow(handleRequests));
        this.#subscription.add(this.#state.addFlow(handleExecution(queryFn)));
        // merge caller-provided retry options over the defaults
        const retryOptions = Object.assign({ count: 0, delay: 0 }, options?.retry);
        this.#subscription.add(this.#state.addFlow(handleFailure(retryOptions)));
        /**
         * Register effects to emit lifecycle events for different query actions.
         *
         * These effects monitor the internal state changes and emit corresponding events
         * that external observers can subscribe to for tracking query progress.
         */
        // Emit event when a query request is initiated
        this.#state.addEffect(actions.request.type, ({ payload, meta: { transaction } }) => {
            this._registerEvent('query_client_job_requested', transaction, payload);
        });
        // Emit event when query execution begins
        this.#state.addEffect(actions.execute.type, ({ meta: { transaction } }) => {
            this._registerEvent('query_client_job_executing', transaction);
        });
        // Emit event when query execution completes successfully
        this.#state.addEffect(actions.execute.success.type, ({ payload, meta: { transaction } }) => {
            this._registerEvent('query_client_job_completed', transaction, { payload });
        });
        // Emit event when query execution fails
        this.#state.addEffect(actions.execute.failure.type, ({ payload, meta: { transaction } }) => {
            const error = payload.error instanceof Error ? payload.error : new Error(String(payload.error));
            this._registerEvent('query_client_job_failed', transaction, { error });
        });
        // Emit event when a query is canceled
        this.#state.addEffect(actions.cancel.type, ({ payload: { reason }, meta: { transaction } }) => {
            this._registerEvent('query_client_job_canceled', transaction, {
                reason: reason || 'no reason provided',
            });
        });
        // Emit event when a general error occurs during query processing
        this.#state.addEffect(actions.error.type, ({ payload, meta: { transaction } }) => {
            const error = payload.error instanceof Error ? payload.error : new Error(String(payload.error));
            this._registerEvent('query_client_job_error', transaction, { error });
        });
    }
    /**
     * Dispatches an action to the internal state subject, which will be processed by the reducer
     * and any registered effects.
     * @param {Actions} action The action to dispatch.
     */
    next(action) {
        this.#state.next(action);
    }
    /**
     * Initiates a query with the provided arguments and options. It returns a QueryClientJob
     * which is an Observable that represents the lifecycle of the query.
     * @param {TArgs} args Call arguments for the query function.
     * @param {Partial<QueryClientOptions>} [opt] Query options.
     * @returns {QueryClientJob<TType, TArgs>} The QueryClientJob representing the initiated query.
     */
    query(args, opt) {
        const job = new QueryClientJob(this, args, opt);
        // share a single underlying job execution across all subscribers, replaying past emissions to late subscribers
        const job$ = job.pipe(share({ connector: () => new ReplaySubject(), resetOnRefCountZero: false }));
        Object.defineProperties(job$, {
            transaction: { get: () => job.transaction },
            created: { get: () => job.created },
            status: { get: () => job.status },
            closed: { get: () => job.closed },
            cancel: { value: (reason) => job.cancel(reason) },
            complete: { value: () => job.complete() },
        });
        // `job$` is a plain shared Observable; the properties defined above give it the
        // `QueryClientJob` shape at runtime, which TypeScript can't infer structurally.
        return job$;
    }
    /**
     * Initiates a query and returns a promise that resolves with the result of the query.
     * It is a convenience method that wraps the query method for use with async/await syntax.
     * @param {TArgs} args Call arguments for the query function.
     * @param {QueryClientOptions} [opt] Query options.
     * @returns {Promise<QueryClientResult<TType>>} A promise that resolves with the result of the query.
     */
    async nextAsync(args, opt) {
        return firstValueFrom(this.query(args, opt));
    }
    /**
     * Retrieves the request associated with the specified transaction.
     *
     * @param transaction - The transaction identifier.
     * @returns The request associated with the specified transaction.
     */
    getRequest(transaction) {
        return this.#state.value[transaction];
    }
    /**
     * Retrieves the request associated with the specified reference string.
     *
     * @param ref - The reference identifier to search for.
     * @returns The request matching the reference, or `undefined` if not found.
     */
    getRequestByRef(ref) {
        const requests = Object.values(this.#state.value);
        // scan all tracked requests for the one matching the given ref
        const match = requests.find((x) => x.ref === ref);
        return match;
    }
    /**
     * Cancels a task by its reference string. If a reason is provided, it will be included
     * in the cancellation effect.
     * @param {string} ref The reference string of the task to cancel.
     * @param {string} [reason] The reason for cancellation.
     */
    cancelTaskByRef(ref, reason) {
        const entry = this.getRequestByRef(ref);
        // Only cancel when a matching request was actually found
        if (entry?.ref) {
            this.cancel(entry.transaction, reason);
        }
    }
    /**
     * Cancels a query. If a transaction id is provided, it cancels the specific query.
     * Otherwise, it cancels all queries.
     * @param {string} [transaction] The transaction id of the query to cancel.
     * @param {string} [reason] The reason for cancellation.
     */
    cancel(transaction, reason) {
        // No transaction targeted — cancel every tracked transaction
        if (!transaction) {
            // If no specific transaction is provided, iterate through all transactions
            // in the state and cancel each one, applying a generic cancellation reason.
            for (const key of Object.keys(this.#state.value)) {
                this.cancel(key, 'all transactions requested canceled');
            }
        }
        else if (this.#state.value[transaction]) {
            // If a specific transaction is provided and it exists in the state,
            // dispatch a cancellation action with an optional reason or a default message.
            reason ??= `cancelation requested for job: ${transaction}`;
            this.#state.next(actions.cancel(transaction, reason));
        }
    }
    /**
     * Registers a callback to be executed when an action of the specified type is dispatched.
     * It allows for custom behavior to be defined in response to specific actions.
     * @template TAction The type of the action to listen for.
     * @param {TAction} type The type of the action.
     * @param {(action: ActionMap[TAction], subject: QueryClient<TType, TArgs>) => void} cb The callback to execute.
     * @returns {Subscription} A Subscription that can be used to unregister the effect.
     */
    on(type, cb) {
        return this.#state.addEffect(actions[type].type, (action) => {
            cb(action, this);
        });
    }
    /**
     * Completes the QueryClient's internal state subject, effectively cleaning up all
     * resources and subscriptions. After calling complete, the QueryClient will no longer
     * accept new actions or emit new state changes.
     */
    complete() {
        this.#subscription.unsubscribe();
    }
}
export default QueryClient;
//# sourceMappingURL=QueryClient.js.map